import { Clickable, ClickHandler } from 'dg-web-shared/ui/Clickable';
import * as Icons24 from 'dg-web-shared/ui/icons/Icons24';
import { css } from '@emotion/css';
import { Colors, Typo } from './vars';
import React, { type JSX, ReactNode, useRef, useEffect, useState } from 'react';
import { Icon24 } from 'dg-web-shared/ui/icons/Icon';
import { createPortal } from 'react-dom';

const SLIDEIN_ANIMATION_TIME = 300;

type ContainerProps<P> = P & { open: boolean };

export interface SlideInProps {
    open: boolean;
    onClose: () => void;
    title: string | JSX.Element;
    children?: ReactNode;
}

function generatePortalSlideInComponent(containerId: string) {
    return function <P extends SlideInProps>(Comp: React.ComponentType<P>) {
        function SlideInComponent(
            props: ContainerProps<P>,
        ): JSX.Element | null {
            // The class mirrored its props into state so the closing frame
            // could keep rendering the last open props. This ref does the same
            // job: it is only ever written while the slide-in is open.
            const lastOpenProps = useRef(props);
            if (props.open) {
                lastOpenProps.current = props;
            }

            const [shouldRender, setShouldRender] = useState(props.open);
            if (props.open && !shouldRender) {
                setShouldRender(true);
            }

            // This effect only re-runs when `open` or `shouldRender` change, so
            // an unrelated re-render cannot restart the close transition — and
            // its cleanup clears the timeout on unmount, which the class never
            // did.
            useEffect(() => {
                if (props.open || !shouldRender) {
                    return;
                }
                const timeoutId = setTimeout(() => {
                    setShouldRender(false);
                }, SLIDEIN_ANIMATION_TIME);
                return () => clearTimeout(timeoutId);
            }, [props.open, shouldRender]);

            if (!shouldRender) {
                return null;
            }

            const rendered: ContainerProps<P> = props.open
                ? props
                : { ...lastOpenProps.current, open: false };

            return createPortal(
                <SelfOpeningSlideIn {...rendered}>
                    <Comp {...rendered} />
                </SelfOpeningSlideIn>,
                document.getElementById(containerId) as HTMLElement,
            );
        }
        SlideInComponent.displayName = 'SlideInComponent';
        return SlideInComponent;
    };
}

const staticStyles = css({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'white',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    transitionProperty: 'transform',
    transitionDuration: '300ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'transform',
});

function SelfOpeningSlideIn(props: SlideInProps) {
    const [open, setOpen] = useState(false);

    // Closing has to take effect in the render pass the prop changes in, as the
    // old UNSAFE_componentWillReceiveProps did, so the outgoing transition
    // starts at the same moment as the parent's unmount timer.
    const [previousOpen, setPreviousOpen] = useState(props.open);
    if (previousOpen !== props.open) {
        setPreviousOpen(props.open);
        if (!props.open) {
            setOpen(false);
        }
    }

    // Opening is deferred by a tick, as the old componentDidMount did, so the
    // browser commits the off-screen transform first and the CSS transition
    // actually runs instead of the panel appearing in place.
    useEffect(() => {
        if (!props.open) {
            return;
        }
        const timeoutId = setTimeout(() => setOpen(true), 0);
        return () => clearTimeout(timeoutId);
    }, [props.open]);

    return (
        <div
            className={css(staticStyles, {
                transform: !open ? 'translateX(100%)' : 'translateX(0)',
            })}
        >
            <div
                className={css({
                    position: 'absolute',
                    height: '48px',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: Colors.darkBlue,
                    display: 'flex',
                    alignItems: 'center',
                })}
            >
                <div
                    className={css({
                        ...Typo.robotoLight,
                        fontSize: '22px',
                        flex: 1,
                        color: Colors.white,
                        userSelect: 'text',
                        marginLeft: '23px',
                    })}
                >
                    {props.title}
                </div>
                <SlideInHeaderClose onClose={() => props.onClose()} />
            </div>
            <div
                className={css({
                    position: 'absolute',
                    top: '48px',
                    bottom: '0px',
                    width: '100%',
                    overflowY: 'auto',
                })}
            >
                <div
                    className={css({
                        position: 'relative',
                        height: '100%',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    })}
                >
                    {props.children}
                </div>
            </div>
        </div>
    );
}

export const slideInPortal = generatePortalSlideInComponent('slidein');

interface SlideInHeaderCloseProps {
    onClose: ClickHandler;
}

export const SlideInHeaderClose = (p: SlideInHeaderCloseProps) => {
    return (
        <Clickable
            element="div"
            className={css({
                color: Colors.white,
                width: '48px',
            })}
            onClick={p.onClose}
        >
            <Icon24 icon={Icons24.clear_b} />
        </Clickable>
    );
};

interface SlideInItemProps {
    children?: ReactNode;
    onClick: () => void;
    selected: boolean;
}

export const SlideInItem = (p: SlideInItemProps): JSX.Element => {
    return (
        <Clickable
            element="div"
            className={css({
                ...Typo.robotoLight,
                fontSize: '18px',
                padding: '16px 24px',
                color: p.selected ? Colors.white : Colors.darkBlue,
                backgroundColor: p.selected ? Colors.lightBlue : 'transparent',
            })}
            onClick={p.onClick}
        >
            {p.children}
        </Clickable>
    );
};
